// src/user/pages/MyAssignments.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SmartDataTable from "../../components/DataTable/SmartDataTable";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";

import { useAuth } from "../../contexts/AuthContext";
import { getMyAssignments } from "../../services/instances.service";
import { deadlineDate, deadlineHour, deadlineExpired, localTimeZone, assignmentClosed, blockingTimestamp, countdown } from "../../services/assignmentDeadline";

/* ------------------- helpers ------------------- */

function statusTag(status) {
  const map = {
    "not-started": { label: "Sin iniciar", severity: "secondary" },
    "in-progress": { label: "En progreso", severity: "warning" },
    submitted: { label: "Enviado", severity: "success" },
  };
  return map[status] || map["not-started"];
}

export default function MyAssignments() {
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const { me, loading } = useAuth();

  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(true);
  const [q, setQ] = useState("");
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);

  /* ====================== Cargar assignments ====================== */
  useEffect(() => {
    if (loading || !me) return;
    let cancelled = false;
    let running = false;

    const run = async () => {
      if (running) return;
      running = true;
      try {
        const rows = await getMyAssignments();

        // Backend devuelve "historic"; el frontend usa "isHistoric"
        const normalized = (rows || []).map((r) => ({
          ...r,
          isHistoric: r.historic ?? false,
        }));

        console.log("[MyAssignments] rows from backend:");
        console.table(
          normalized.map((r) => ({
            id: r.id,
            questionnaireId: r.questionnaireId,
            questionnaireName: r.questionnaireName,
            forUser: r.forUser,
            progress: `${r.progress}/${r.total}`,
            due: r.due,
            status: r.status,
            submittedAt: r.submittedAt,
            processInstanceId: r.processInstanceId,
            historic: r.historic,
          }))
        );

        if (!cancelled) setData(normalized);
      } catch (err) {
        console.error("[MyAssignments] Error fetching my assignments", err);
      } finally {
        running = false;
        if (!cancelled) setDataLoading(false);
      }
    };

    setDataLoading(true);
    run();
    const timer = setInterval(run, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [me, loading]);

  /* ==================== Filtros y tabla ==================== */

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let base = data;

    if (!showCompleted) {
      base = base.filter((r) => r.status !== "submitted");
    }

    if (!s) return base;

    return base.filter((r) =>
      [r.questionnaireName, r.forUser, r.status, r.due]
        .join(" ")
        .toLowerCase()
        .includes(s)
    );
  }, [q, data, showCompleted]);

  const columns = useMemo(
    () => [
      {
        key: "questionnaireName",
        header: "Cuestionario",
        body: (row) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.questionnaireName}</span>
          </div>
        ),
        sortable: true,
      },
      {
        field: "forUser",
        header: "Para",
        sortable: true,
        headerStyle: { width: "14rem" },
      },
      // Columna de progreso comentada temporalmente
      /*
      {
        key: "progress",
        header: "Progreso",
        headerStyle: { width: "12rem", textAlign: "right" },
        bodyStyle: { textAlign: "right" },
        body: (row) => {
          const pct = row.total
            ? Math.round((row.progress / row.total) * 100)
            : 0;
          return (
            <div className="inline-flex items-center gap-2 justify-end w-full">
              <span className="text-sm tabular-nums">
                {row.progress}/{row.total}
              </span>
              <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-secondary dark:bg-secondary"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        },
        sortable: false,
      },
      */
      {
        key: "due",
        header: "Vence",
        sortable: true,
        headerStyle: { width: "10rem" },
        body: (row) => (
          <span
            className={
              "text-sm " +
              (row.status !== "submitted" && deadlineExpired(row, now)
                ? "text-red-600 dark:text-red-400 font-medium"
                : "")
            }
          >
            {deadlineDate(row)}
          </span>
        ),
      },
      {
        field: "dueAt", header: "Hora límite", sortable: true, headerStyle: { width: "10rem" },
        body: row => <span title={localTimeZone()} className={row.status !== "submitted" && deadlineExpired(row, now) ? "text-red-600 dark:text-red-400 font-medium" : ""}>
          {deadlineHour(row)}
        </span>,
      },
      {
        key: "status",
        header: "Estado",
        headerStyle: { width: "10rem" },
        body: (row) => {
          const s = row.status !== "submitted" && assignmentClosed(row, now)
            ? { label: "Cerrando", severity: "warning" } : statusTag(row.status);
          return <Tag value={s.label} severity={s.severity} />;
        },
        sortable: true,
      },
      {
        field: "timeLimitMinutes", header: "Temporizador", sortable: true, headerStyle: { width: "11rem" },
        body: row => row.timeLimitMinutes ? <span className={row.status !== "submitted" && assignmentClosed(row, now) ? "text-red-600 dark:text-red-400" : ""}>
          {row.startedAt && row.status !== "submitted" ? countdown((blockingTimestamp(row) - now - (row.clockOffset || 0)) / 1000) : `${row.timeLimitMinutes} min`}
        </span> : "—",
      },
    ],
    [now]
  );

  const actionsColumn = useMemo(
    () => ({
      header: "Acciones",
      exportable: false,
      headerStyle: { width: "12rem", textAlign: "center" },
      bodyStyle: { textAlign: "center" },
      body: (row) => {
        const isSubmitted = row.status === "submitted";
        return (
          <div className="flex justify-center">
            <Button
              icon="pi pi-play"
              label={
                isSubmitted
                  ? "Ver"
                  : row.status === "in-progress"
                  ? "Continuar"
                  : "Iniciar"
              }
              outlined
              size="small"
              disabled={!isSubmitted && assignmentClosed(row, now)}
              onClick={() =>
                navigate(`/assignments/${row.id}`, {
                  state: {
                    questionnaireId: row.questionnaireId,
                    questionnaireName: row.questionnaireName,
                    forUser: row.forUser,
                    forUserId: row.forUserId,
                    due: row.due,
                    dueAt: row.dueAt,
                    hardDeadline: row.hardDeadline,
                    clockOffset: row.clockOffset,
                    timeLimitMinutes: row.timeLimitMinutes,
                    startedAt: row.startedAt,
                    timerExpiresAt: row.timerExpiresAt,
                    deadlineAt: row.deadlineAt,
                    status: row.status,
                    submittedAt: row.submittedAt,
                    processInstanceId: row.processInstanceId,
                    isHistoric: row.isHistoric || false,
                    readOnly: row.status === "submitted",
                  },
                })
              }
            />
          </div>
        );
      },
    }),
    [navigate, now]
  );

  const Header = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="p-input-icon-left roles-search">
          <i className="pi pi-search" />
          <InputText
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar asignaciones..."
            className="p-inputtext-sm w-72"
          />
        </span>
        {q && (
          <Button
            type="button"
            icon="pi pi-times"
            label="Limpiar"
            text
            onClick={() => setQ("")}
          />
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            inputId="showCompleted"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.checked)}
          />
          <label
            htmlFor="showCompleted"
            className="text-xs sm:text-sm text-muted-foreground cursor-pointer"
          >
            Mostrar completadas
          </label>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        Haz clic en <span className="font-medium">Iniciar/Continuar</span> para abrir el cuestionario.
      </div>
    </div>
  );

  const filled = data.filter((r) => r.status === "submitted").length;
  const total = data.length;
  const PAGE_SIZE = 10;

  const tablePt = {
    root: {
      className:
        "h-full rounded-md border overflow-hidden flex flex-col bg-white dark:bg-gray-900",
    },
    wrapper: { className: "h-full" },
    table: { className: "w-full text-sm" },
    thead: {
      className: "bg-gray-50 dark:bg-gray-800 border-b border-gray-200",
      style: { borderColor: "white !important" },
    },
    headerCell: {
      className:
        "h-12 px-4 text-left align-middle font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap",
    },
    tbody: { className: "bg-white dark:bg-gray-900" },
    row: ({ context }) => ({
      className: [
        "border-b transition-colors",
        context.index % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/50" : "",
        "hover:bg-gray-100 dark:hover:bg-gray-800",
      ].join(" "),
    }),
    bodyCell: { className: "p-4 align-middle" },
    emptyMessage: { className: "p-6 text-center text-sm text-gray-500" },
  };

  return (
    <div className="my-assignments-page h-full min-h-0 flex flex-col p-4 sm:p-6 gap-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-5 flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl font-bold m-0">Mis asignaciones</h2>
          <span className="text-sm text-muted-foreground">
            Completadas {filled} de {total}
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{" "}
            Enviadas
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />{" "}
            En progreso
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />{" "}
            Sin iniciar
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative">
        <SmartDataTable
          ref={tableRef}
          value={filtered}
          dataKey="id"
          columns={columns}
          trailingColumns={[actionsColumn]}
          header={Header}
          scrollable
          scrollHeight="flex"
          className="h-full"
          tableStyle={{ minWidth: "64rem" }}
          paginator
          rows={PAGE_SIZE}
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          sortMode="multiple"
          showGridlines
          stripedRows
          removableSort
          emptyMessage="No se encontraron asignaciones"
          pt={tablePt}
        />

        {(loading || dataLoading) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <ProgressSpinner
                style={{ width: "3rem", height: "3rem" }}
                strokeWidth="4"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Cargando asignaciones...
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
